// Reads a V6 fill shapeSpec - the BOM outline of a non-rectangular pane - and turns it
// into an SVG path, so same-size panes of different shape can be told apart on a line.
//
// Format (written by V6 Tv6DesignFillBase.ShapeSpecForPoints):
//   points: (x, y),  (x, y),  ...[  curves: none,  center (x, y) radius r,  ...]
// Points start at the bottom-left of the shape's extent, y up. Curve entry i describes the
// edge from point i to point i + 1, and V6 always draws it counter-clockwise around the centre.
//
// Why parse V6 display strings here? Because the values are written in the supplier's
// measurement format (42 33/64", 3' 6 1/2", 1080 mm), and a picture only needs proportions,
// so a tolerant local read beats a V6 round trip per line. Anything unreadable yields null
// and the line simply shows no picture.

export interface FillShapePoint {
  x: number;
  y: number;
}

export interface FillShapeArc {
  cx: number;
  cy: number;
  r: number;
}

export interface FillShape {
  points: FillShapePoint[];
  /** One entry per edge (point i to point i + 1); null for a straight edge. */
  arcs: (FillShapeArc | null)[];
}

const MM_PER_UNIT: Record<string, number> = { '': 1, mm: 1, cm: 10, m: 1000, '"': 25.4, in: 25.4 };

// Optional sign and feet part ("3' " or "3'-"), then the rest.
const FEET = /^(-)?\s*(?:(\d+(?:\.\d+)?)\s*'\s*-?\s*)?(.*)$/;
// A number with an optional fraction ("6 1/2"), or a bare fraction ("33/64"), then a unit.
const VALUE = /^(?:(\d+(?:\.\d+)?)(?:\s+(\d+)\/(\d+))?|(\d+)\/(\d+))?\s*(mm|cm|m|"|in)?$/;

/**
 * Reads one V6 measurement display string as millimetres (bare numbers are taken as
 * millimetres; only proportions matter to the caller). Returns null when unreadable.
 */
export function parseSpecMeasurement(text: string): number | null {
  const feetMatch = FEET.exec(text.trim());
  if (!feetMatch) return null;
  const [, sign, feet, rest] = feetMatch;
  const match = VALUE.exec(rest);
  if (!match) return null;
  const [, whole, wholeNumerator, wholeDenominator, bareNumerator, bareDenominator, unit] = match;
  const numerator = wholeNumerator ?? bareNumerator;
  const denominator = wholeDenominator ?? bareDenominator;
  if (feet === undefined && whole === undefined && numerator === undefined) return null;
  if (denominator !== undefined && Number(denominator) === 0) return null;

  const value = Number(whole ?? 0) + (numerator !== undefined ? Number(numerator) / Number(denominator) : 0);
  // A feet part or a fraction means imperial even when the trailing inch mark is absent.
  const imperial = feet !== undefined || numerator !== undefined;
  const mm = imperial ? (Number(feet ?? 0) * 12 + value) * 25.4 : value * MM_PER_UNIT[unit ?? ''];
  return sign ? -mm : mm;
}

function parsePoint(text: string): FillShapePoint | null {
  const parts = text.split(/,\s+/);
  if (parts.length !== 2) return null;
  const x = parseSpecMeasurement(parts[0]);
  const y = parseSpecMeasurement(parts[1]);
  return x === null || y === null ? null : { x, y };
}

/** Parses a V6 shapeSpec, or returns null when it is absent or cannot be read with confidence. */
export function parseShapeSpec(spec: string | null | undefined): FillShape | null {
  const text = spec?.trim() ?? '';
  if (!text.startsWith('points:')) return null;

  const curvesAt = text.indexOf('curves:');
  const pointsText = text.slice('points:'.length, curvesAt < 0 ? undefined : curvesAt);
  const points: FillShapePoint[] = [];
  for (const match of pointsText.matchAll(/\(([^)]*)\)/g)) {
    const point = parsePoint(match[1]);
    if (!point) return null;
    points.push(point);
  }
  if (points.length < 3) return null;

  if (curvesAt < 0) return { points, arcs: points.map(() => null) };

  const arcs: (FillShapeArc | null)[] = [];
  const curvesText = text.slice(curvesAt + 'curves:'.length);
  for (const match of curvesText.matchAll(/none|center\s*\(([^)]*)\)\s*radius\s*([^,]+)/g)) {
    if (match[0] === 'none') {
      arcs.push(null);
      continue;
    }
    const centre = parsePoint(match[1]);
    const r = parseSpecMeasurement(match[2]);
    if (!centre || r === null || r <= 0) return null;
    arcs.push({ cx: centre.x, cy: centre.y, r });
  }
  // Why reject a count mismatch rather than draw what we have? Because pairing a curve
  // with the wrong edge draws a confidently wrong pane; no picture is the safer answer.
  return arcs.length === points.length ? { points, arcs } : null;
}

/**
 * The shape to draw for a fill line: its V6 outline when it has one, otherwise a rectangle
 * of its size (rectangles carry no spec). Null when there is nothing safe to draw.
 */
export function fillShapeForLine(
  shapeSpec: string | null | undefined,
  width: number | null | undefined,
  height: number | null | undefined
): FillShape | null {
  // Why not fall back to a rectangle when the spec is unreadable? Because that line is
  // known to be shaped, and a rectangle would tell the orderer the wrong thing.
  if (shapeSpec) return parseShapeSpec(shapeSpec);
  if (!width || !height || width < 0 || height < 0) return null;
  return {
    points: [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height }
    ],
    arcs: [null, null, null, null]
  };
}

const TWO_PI = Math.PI * 2;

// Counter-clockwise angle travelled from a0 to a1, in [0, 2pi).
function ccwSweep(a0: number, a1: number): number {
  return (((a1 - a0) % TWO_PI) + TWO_PI) % TWO_PI;
}

// Trims float noise so the path text stays short and stable.
function fmt(value: number): string {
  return `${Math.round(value * 1e4) / 1e4}`;
}

/**
 * Builds an SVG viewBox and path for a parsed shape, flipped so y points down as SVG
 * expects. The viewBox is widened to fit any arc that bulges past the points.
 */
export function fillShapeSvg(shape: FillShape): { viewBox: string; path: string } {
  const { points, arcs } = shape;
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);

  // Each curved edge's counter-clockwise sweep, shared by the box and the path below.
  const sweeps = arcs.map((arc, i) => {
    if (!arc) return 0;
    const from = points[i];
    const to = points[(i + 1) % points.length];
    const a0 = Math.atan2(from.y - arc.cy, from.x - arc.cx);
    const sweep = ccwSweep(a0, Math.atan2(to.y - arc.cy, to.x - arc.cx));
    // An axis-aligned extreme of the circle lies on the arc when it falls inside the sweep.
    for (let quarter = 0; quarter < 4; quarter++) {
      const angle = (quarter * Math.PI) / 2;
      if (ccwSweep(a0, angle) <= sweep) {
        xs.push(arc.cx + arc.r * Math.cos(angle));
        ys.push(arc.cy + arc.r * Math.sin(angle));
      }
    }
    return sweep;
  });

  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const sx = (x: number) => fmt(x - minX);
  const sy = (y: number) => fmt(maxY - y);

  const segments = [`M ${sx(points[0].x)} ${sy(points[0].y)}`];
  points.forEach((_, i) => {
    const to = points[(i + 1) % points.length];
    const arc = arcs[i];
    if (!arc) {
      if (i < points.length - 1) segments.push(`L ${sx(to.x)} ${sy(to.y)}`);
      return;
    }
    const largeArc = sweeps[i] > Math.PI ? 1 : 0;
    // Why sweep flag 0? Because the y flip only undoes SVG's y-down axis, so V6's
    // counter-clockwise still looks counter-clockwise, which is SVG's negative-angle direction.
    segments.push(`A ${fmt(arc.r)} ${fmt(arc.r)} 0 ${largeArc} 0 ${sx(to.x)} ${sy(to.y)}`);
  });
  segments.push('Z');

  return {
    viewBox: `0 0 ${fmt(Math.max(...xs) - minX)} ${fmt(maxY - minY)}`,
    path: segments.join(' ')
  };
}
